import { memo, FC, ReactElement, JSXElementConstructor } from 'react';

import { find, reverse } from 'lodash';
import { useController, useFormContext } from 'react-hook-form';

import { CustomComboboxItem, Group, Select, SelectProps, Text } from '@ui';
import { convertFeetAndInchesToCm } from '@utils';

export interface HeightPickerProps extends SelectProps {
  ftName: string;
  inName: string;
  label: string | ReactElement<any, string | JSXElementConstructor<any>>;
  placeholder?: string;
  disabled?: boolean;
  defaultSelected?: string;
  customOptions?: HeightOption[];
}

interface HeightOption {
  label: string;
  value: string;
  parts: { feet: number; inch: number };
}

const getHeightOptions = (): [HeightOption] => {
  const feetSet = [1, 2, 3, 4, 5, 6, 7];
  const inchSet = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const options: any = [];
  feetSet.forEach((feet) => {
    inchSet.forEach((inch) => {
      options.push({
        label: `${feet} feet ${inch} inch`,
        value: `${feet}:${inch}`,
        parts: { feet, inch }
      });
    });
  });

  const descendingHeightOptions = reverse(options);
  return descendingHeightOptions;
};

const HeightPicker: FC<HeightPickerProps> = memo((props: HeightPickerProps) => {
  const {
    ftName,
    inName,
    label,
    placeholder,
    disabled,
    defaultSelected,
    customOptions,
    ...rest
  } = props;

  const { setValue: _setValue, control } = useFormContext();
  const { field: feet, fieldState: feetFieldState } = useController({
    name: ftName,
    control
  });

  const { field: inch, fieldState: inchFieldState } = useController({
    name: inName,
    control
  });

  const setValue = (name: string, value: string) => {
    _setValue(name, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  const options = customOptions ?? getHeightOptions();

  const selectData = [
    ...options.map((option: HeightOption) => ({
      value: option.value,
      label: option.label,
      parts: option.parts
    }))
  ];

  const handleChange = (value: string | null) => {
    if (value && value !== '') {
      const option = find(options, (o: HeightOption) => o.value === value);
      if (option) {
        setValue(ftName, option.parts.feet.toString());
        setValue(inName, option.parts.inch.toString());
      }
    } else {
      setValue(ftName, '');
      setValue(inName, '');
    }
  };

  const renderSelectOption = (option: CustomComboboxItem) => (
    <Group flex="1" gap="xs" wrap="nowrap" align="center" justify="space-between">
      {option.label}
      {option.parts && (
        <Text span lightColor="secondary.6" darkColor="dark.2" fz={12}>
          {convertFeetAndInchesToCm(option.parts.feet, option.parts.inch)} cm
        </Text>
      )}
    </Group>
  );

  const hasError = feetFieldState.error?.message || inchFieldState.error?.message;
  const selectedValue = `${feet.value}:${inch.value}`;

  return (
    <Select
      label={label}
      placeholder={placeholder}
      data={selectData}
      value={selectedValue || defaultSelected || null}
      onChange={handleChange}
      disabled={disabled}
      error={hasError}
      searchable
      clearable
      itemComponent={renderSelectOption}
      onBlur={() => {
        feet.onBlur();
        inch.onBlur();
      }}
      w="100%"
      {...rest}
    />
  );
});

export { HeightPicker };
